// src/components/products/CreateCategory.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { showToast } from '../../utils/helpers';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';

// Constants for better maintainability
const CATEGORY_TIPS = [
  '• Use clear, descriptive category names',
  '• Categories help organize your products',
  '• Customers can filter products by category'
];

const CreateCategory = () => {
  const [categoryName, setCategoryName] = useState('');
  const [collections, setCollections] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  // Memoized values
  const displayCollections = useMemo(() => 
    Array.isArray(collections) ? collections : [], 
    [collections]
  );

  const isEmpty = useMemo(() => 
    displayCollections.length === 0, 
    [displayCollections]
  );

  const totalProducts = useMemo(() => 
    displayCollections.reduce((acc, curr) => 
      acc + (curr.total_products || 0), 0
    ), 
    [displayCollections]
  );

  // Data fetching
  const fetchCategories = useCallback(async () => {
    try {
      setIsFetching(true);
      const response = await api.post(API_CONFIG.PRODUCTS.COLLECTIONS);
      const data = response.data;
      
      if (data.success) {
        setCollections(data.collections || []);
      } else {
        showToast('Failed to fetch categories', 'error');
        setCollections([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      showToast('An error occurred while fetching categories', 'error');
      setCollections([]);
    } finally {
      setIsFetching(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Form submission
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!categoryName.trim()) {
      showToast('Category name cannot be empty', 'error');
      return;
    }

    if (categoryName.length < 1) {
      showToast('Category name must be at least 1 character', 'error');
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post(API_CONFIG.PRODUCTS.SAVE_CATEGORY, {
        collectionName: categoryName.trim()
      });

      const data = response.data;
      
      if (data.success) {
        showToast(data.message || 'Category created successfully', 'success');
        setCategoryName('');
        await fetchCategories();
      } else {
        showToast(data.error || 'Failed to create category', 'error');
      }
    } catch (error) {
      console.error('Error creating category:', error);
      showToast('An error occurred. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [categoryName, fetchCategories]);

  const handleCancel = useCallback(() => {
    setCategoryName('');
  }, []);

  const getCategoryInitial = useCallback((category) => {
    const name = category.collection_name || category.collectionName;
    return name ? name.charAt(0).toUpperCase() : 'C';
  }, []);

  const getCategoryName = useCallback((category) => {
    return category.collection_name || category.collectionName || 'Unnamed Category';
  }, []);

  const getProductCount = useCallback((category) => {
    return category.total_products?.toLocaleString() || '0';
  }, []);

  // Loading spinner component
  const LoadingSpinner = () => (
    <div className="flex items-center justify-center py-8">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
    </div>
  );

  // Empty state component
  const EmptyState = () => (
    <div className="text-center py-8">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      </div>
      <h3 className="text-sm font-medium text-gray-900 mb-2" style={{ fontSize: '13px' }}>
        No categories yet
      </h3>
      <p className="text-xs text-gray-500" style={{ fontSize: '11px' }}>
        Create your first category to organize products
      </p>
    </div>
  );

  // Category list component
  const CategoryList = () => (
    <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
      {displayCollections.map((collection) => (
        <div 
          key={collection.collection_id || collection.id}
          className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-100 transition-all duration-200 group"
        >
          <div className="flex items-center min-w-0 flex-1">
            <div 
              className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 group-hover:bg-blue-200 transition-colors"
              aria-hidden="true"
            >
              <span className="text-blue-800 font-semibold text-sm">
                {getCategoryInitial(collection)}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <h4 
                className="text-sm font-medium text-gray-900 truncate"
                style={{ fontSize: '12px' }}
                title={getCategoryName(collection)}
              >
                {getCategoryName(collection)}
              </h4>
              <p className="text-xs text-gray-500 mt-1" style={{ fontSize: '10px' }}>
                {getProductCount(collection)} products
              </p>
            </div>
          </div>
          <div 
            className="bg-white px-2 py-1 rounded border border-gray-300 text-xs font-medium text-gray-700 flex-shrink-0 ml-3"
            style={{ fontSize: '10px' }}
          >
            {getProductCount(collection)}
          </div>
        </div>
      ))}
    </div>
  );

  // Stats component
  const StatsSection = () => (
    <div className="grid grid-cols-2 gap-4 px-4 border-t border-gray-200">
      <div className="text-center">
        <div className="text-xl font-bold text-blue-600">{displayCollections.length}</div>
        <div className="text-xs text-gray-600 mt-1" style={{ fontSize: '11px' }}>Total Categories</div>
      </div>
      <div className="text-center">
        <div className="text-xl font-bold text-green-600">
          {totalProducts.toLocaleString()}
        </div>
        <div className="text-xs text-gray-600 mt-1" style={{ fontSize: '11px' }}>Total Products</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-full ">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h1 
                className="text-lg font-semibold text-gray-900 truncate"
                style={{ fontSize: '16px' }}
              >
                Manage Categories
              </h1>
              <p 
                className="text-sm text-gray-600 mt-1 truncate"
                style={{ fontSize: '12px' }}
              >
                Create and organize product categories
              </p>
            </div>
            
            {/* Desktop Stats Summary */}
            <div className="hidden lg:flex items-center space-x-6 text-sm">
              <div className="text-center">
                <div className="text-lg font-bold text-blue-600">{displayCollections.length}</div>
                <div className="text-xs text-gray-500">Categories</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-green-600">
                  {totalProducts.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500">Products</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-2 py-2 bg-transparent">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Create Category Form - Left Side */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 
                className="text-base font-semibold text-gray-900"
                style={{ fontSize: '15px' }}
              >
                Create New Category
              </h2>
              <div 
                className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center"
                aria-hidden="true"
              >
                <i className="bi bi-folder-plus text-blue-600 text-sm"></i>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label 
                  htmlFor="categoryName"
                  className="block text-sm font-medium text-gray-700 mb-2"
                  style={{ fontSize: '13px' }}
                >
                  Category Name
                </label>
                <input
                  type="text"
                  id="categoryName"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  placeholder="e.g., Electronics, Clothing, Furniture"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm placeholder-gray-400"
                  style={{ fontSize: '13px' }}
                  disabled={isLoading}
                  maxLength={50}
                  aria-required="true"
                  aria-describedby="categoryNameHelp"
                />
                <div 
                  id="categoryNameHelp" 
                  className="text-xs text-gray-500 mt-2 text-right"
                  style={{ fontSize: '11px' }}
                >
                  {categoryName.length}/50 characters
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-2">
                <button 
                  type="submit"
                  disabled={isLoading || !categoryName.trim()}
                  className="flex-1 px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center text-sm"
                  style={{ fontSize: '13px' }}
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-lg mr-2"></i>
                      Create Category
                    </>
                  )}
                </button>
                
                <button 
                  type="button"
                  onClick={handleCancel}
                  disabled={isLoading}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 transition-all duration-200 text-sm"
                  style={{ fontSize: '13px' }}
                >
                  Clear
                </button>
              </div>
            </form>

            {/* Tips Section */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex items-start space-x-3">
                <div 
                  className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                  aria-hidden="true"
                >
                  <span className="text-blue-600 text-sm">💡</span>
                </div>
                <div>
                  <h3 
                    className="text-sm font-medium text-blue-800 mb-2"
                    style={{ fontSize: '13px' }}
                  >
                    Category Best Practices
                  </h3>
                  <ul className="text-sm text-blue-700 space-y-1" style={{ fontSize: '12px' }}>
                    {CATEGORY_TIPS.map((tip, index) => (
                      <li key={index}>{tip}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Existing Categories - Right Side */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 
                className="text-base font-semibold text-gray-900"
                style={{ fontSize: '15px' }}
              >
                Existing Categories
              </h2>
              <button
                onClick={fetchCategories}
                disabled={isFetching}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
                aria-label="Refresh categories"
              >
                <i className={`bi bi-arrow-clockwise ${isFetching ? 'animate-spin' : ''}`}></i>
              </button>
            </div>

            {isFetching ? (
              <LoadingSpinner />
            ) : isEmpty ? (
              <EmptyState />
            ) : (
              <>
                <CategoryList />
                <StatsSection />
              </>
            )}
          </div>
        </div>

        {/* Mobile Stats - Only show on mobile */}
        <div className="lg:hidden mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <StatsSection />
        </div>
      </div>
    </div>
  );
};

export default CreateCategory;